import Cookies from "universal-cookie";

const cookies = new Cookies();
const BASE = process.env.AI_SERVER_URL || "";

const clientError = ({ status, code, message, detail, retryable = false }) => ({
  status,
  code,
  message,
  ...(detail === undefined ? {} : { detail }),
  retryable,
});

/**
 * Escopos e mapeamentos inválidos não são conflitos transitórios. Mantemos as
 * mensagens no cliente, sem ecoar detalhes internos do back-end, e explicamos
 * que essas recusas acontecem antes do consumo da cota.
 */
const SCOPE_ERROR_MESSAGES = Object.freeze({
  ai_company_unavailable:
    "A empresa desta obra não pôde ser determinada. Esta tentativa não consumiu sua cota.",
  ai_mapping_unavailable:
    "O mapeamento financeiro desta obra ainda não está configurado. Esta tentativa não consumiu sua cota.",
  ai_mapping_ambiguous:
    "O mapeamento financeiro desta obra está ambíguo. Esta tentativa não consumiu sua cota.",
  ai_mapping_invalid:
    "O mapeamento financeiro desta obra é inválido. Esta tentativa não consumiu sua cota.",
  ai_project_mapping_unavailable:
    "A obra não possui um mapeamento financeiro inequívoco para análise. Esta tentativa não consumiu sua cota.",
  ai_project_mapping_ambiguous:
    "A obra possui mais de um mapeamento financeiro possível para análise. Esta tentativa não consumiu sua cota.",
  ai_project_unavailable:
    "A identificação da obra não pôde ser determinada. Esta tentativa não consumiu sua cota.",
  ai_building_unavailable:
    "A identificação do building da obra não pôde ser determinada. Esta tentativa não consumiu sua cota.",
  ai_project_scope_unavailable:
    "A obra não possui raízes WBS autorizadas. Esta tentativa não consumiu sua cota.",
  project_scope_unavailable:
    "A obra não possui raízes WBS autorizadas. Esta tentativa não consumiu sua cota.",
  ai_plan_invalid:
    "A configuração de cota da IA desta obra é inválida. Esta tentativa não consumiu sua cota.",
  ai_plan_unavailable:
    "O plano de IA configurado para esta obra não está disponível. Esta tentativa não consumiu sua cota.",
  ai_access_invalid:
    "A configuração de IA desta obra é inválida. Esta tentativa não consumiu sua cota.",
  bill_out_of_scope:
    "Não foi possível determinar o escopo desta obra para a análise. Esta tentativa não consumiu sua cota.",
});

const safeMessage = (status, code) => {
  if (Object.prototype.hasOwnProperty.call(SCOPE_ERROR_MESSAGES, code)) {
    return SCOPE_ERROR_MESSAGES[code];
  }
  if (code === "resource_group_no_evidence") {
    return "Ainda não há dados comparáveis suficientes neste grupo. Esta tentativa não consumiu sua cota.";
  }
  if (code === "resource_group_too_large") {
    return "Este grupo tem mais de 500 insumos. Abra um nível menor da EAP para analisar sem recortes. Esta tentativa não consumiu sua cota.";
  }
  if (code === "resource_group_evidence_invalid") {
    return "Não foi possível montar a evidência deste grupo por inconsistência nos dados dos insumos. Esta tentativa não consumiu sua cota.";
  }
  if (code === "eap_catalog_incomplete") {
    return "Não foi possível carregar todos os itens EAP. Ajuste as unidades e tente novamente; esta tentativa não consumiu sua cota.";
  }
  if (code === "eap_catalog_too_large") {
    return "Há itens EAP demais nas unidades escolhidas. Selecione menos unidades ou descreva melhor o serviço; esta tentativa não consumiu sua cota.";
  }
  if (code === "no_appropriation_candidate") {
    return "Não houve destino EAP suficiente para sugerir um rateio. Descreva melhor o serviço ou escolha outras unidades.";
  }
  if (status === 401) return "Faça login novamente para usar a IA.";
  if (status === 403) return "As análises com IA não estão habilitadas nesta obra.";
  if (status === 404) return "A obra ou a análise não foi encontrada.";
  if (status === 409) return "Os dados desta análise mudaram. Recarregue e tente novamente.";
  if (status === 422) return "Não foi possível determinar o contexto desta análise.";
  if (status === 429) return "A cota de análises desta obra terminou neste período.";
  return "A IA está temporariamente indisponível.";
};

const MAX_SERVER_DETAIL_LENGTH = 200;

/**
 * A mensagem do servidor só é exibida quando o código é
 * resource_group_evidence_invalid (única causa hoje detalhada pelo back-end
 * com texto seguro para o usuário) e é curta o bastante para não carregar
 * detalhe interno arbitrário na tela.
 */
const serverDetail = (code, serverMessage) => {
  if (code !== "resource_group_evidence_invalid") return undefined;
  if (typeof serverMessage !== "string") return undefined;
  if (serverMessage.length === 0 || serverMessage.length > MAX_SERVER_DETAIL_LENGTH) return undefined;
  return serverMessage;
};

const configuredBase = () => {
  if (!BASE) {
    throw clientError({
      status: 0,
      code: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
  try {
    const base = new URL(BASE);
    if (!new Set(["https:", "http:"]).has(base.protocol) || base.username || base.password) {
      throw new Error();
    }
    return base;
  } catch {
    throw clientError({
      status: 0,
      code: "configuration_error",
      message: "Análises com IA não configuradas.",
    });
  }
};

const aiFetch = async (path, { method = "GET", body, idempotencyKey, signal } = {}) => {
  const base = configuredBase();
  const basePath = base.pathname.replace(/\/+$/, "");
  const url = new URL(`${basePath}/${path}`, `${base.origin}/`);
  if (url.origin !== base.origin) {
    throw clientError({ status: 0, code: "configuration_error", message: "Análises com IA não configuradas." });
  }
  const { token } = cookies.get("userId") || {};
  if (typeof token !== "string" || !token) {
    throw clientError({ status: 401, code: "authentication_required", message: safeMessage(401) });
  }
  const headers = { Authorization: `Bearer ${token}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (idempotencyKey) headers["Idempotency-Key"] = idempotencyKey;
  let response;
  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (error) {
    if (error?.name === "AbortError") {
      throw clientError({ status: 0, code: "request_cancelled", message: "Análise cancelada." });
    }
    throw clientError({
      status: 0,
      code: "network_error",
      message: "Não foi possível comunicar com a IA.",
      retryable: true,
    });
  }
  const data = await response.json().catch(() => null);
  if (!response.ok) {
    const code = typeof data?.errorCode === "string" ? data.errorCode : "request_failed";
    throw clientError({
      status: response.status,
      code,
      message: safeMessage(response.status, code),
      detail: serverDetail(code, data?.message),
      retryable: response.status >= 500,
    });
  }
  return data;
};

const workPath = (workId) => `works/${encodeURIComponent(String(workId))}/ai`;

export const fetchAiAccess = (workId, options = {}) => (
  aiFetch(`${workPath(workId)}/access`, options)
);

export const fetchAiHistory = (workId, { contextId, resourceId }, options = {}) => {
  const query = new URLSearchParams({
    context_type: "item_variance",
    context_id: String(contextId),
    resource_id: String(resourceId),
  });
  return aiFetch(`${workPath(workId)}/analyses?${query}`, options);
};

export const createAiAnalysis = (
  workId,
  { contextId, resourceId, questionKey, question },
  { idempotencyKey, signal } = {},
) => aiFetch(`${workPath(workId)}/analyses`, {
  method: "POST",
  idempotencyKey,
  signal,
  body: {
    contextType: "item_variance",
    contextId,
    resourceId,
    questionKey,
    ...(question === undefined ? {} : { question }),
  },
});

export const fetchFinancialMonthAiHistory = (workId, competence, options = {}) => {
  const query = new URLSearchParams({
    context_type: "financial_month",
    context_id: String(competence),
  });
  return aiFetch(`${workPath(workId)}/analyses?${query}`, options);
};

export const createFinancialMonthAiAnalysis = (
  workId,
  { competence, questionKey, question },
  { idempotencyKey, signal } = {},
) => aiFetch(`${workPath(workId)}/analyses`, {
  method: "POST",
  idempotencyKey,
  signal,
  body: {
    contextType: "financial_month",
    contextId: competence,
    questionKey,
    ...(question === undefined ? {} : { question }),
  },
});

const GROUP_CONTEXT_ID = /^[A-Za-z0-9][A-Za-z0-9._:-]{2,199}$/;

const resourceGroupContextId = (locator) => {
  const keys = locator && typeof locator === "object" ? Object.keys(locator) : [];
  if (
    keys.length !== 1
    || keys[0] !== "contextId"
    || typeof locator.contextId !== "string"
    || !GROUP_CONTEXT_ID.test(locator.contextId)
  ) {
    throw clientError({
      status: 0,
      code: "invalid_group_locator",
      message: "Não foi possível identificar o grupo de insumos.",
    });
  }
  return locator.contextId;
};

export const fetchResourceGroupAiHistory = async (workId, locator, options = {}) => {
  const contextId = resourceGroupContextId(locator);
  const query = new URLSearchParams({
    context_type: "resource_group_variance",
    context_id: contextId,
  });
  return aiFetch(`${workPath(workId)}/analyses?${query}`, options);
};

export const createResourceGroupAiAnalysis = async (
  workId,
  locator,
  { idempotencyKey, signal } = {},
) => {
  const contextId = resourceGroupContextId(locator);
  return aiFetch(`${workPath(workId)}/analyses`, {
    method: "POST",
    idempotencyKey,
    signal,
    body: {
      contextType: "resource_group_variance",
      contextId,
      questionKey: "group_cost_drivers",
    },
  });
};

const billReappropriationLocator = (locator, { requireQuestion } = {}) => {
  const keys = locator && typeof locator === "object" ? Object.keys(locator) : [];
  const expected = requireQuestion
    ? ["billId", "question", "mode", "buildingUnitIds"]
    : ["billId"];
  const exactKeys = keys.length === expected.length && expected.every((key) => keys.includes(key));
  const validBillId = locator?.billId !== undefined && locator?.billId !== null && String(locator.billId).trim();
  if (!exactKeys || !validBillId) {
    throw clientError({
      status: 0,
      code: "invalid_bill_reappropriation_locator",
      message: "Não foi possível identificar o título para reapropriação.",
    });
  }
  if (!requireQuestion) return { billId: locator.billId };
  const question = typeof locator.question === "string" ? locator.question.trim() : "";
  const unitIds = Array.isArray(locator.buildingUnitIds) ? locator.buildingUnitIds : [];
  const validUnitIds = unitIds.length >= 1
    && unitIds.length <= 3
    && unitIds.every((unitId) => Number.isInteger(unitId))
    && new Set(unitIds).size === unitIds.length;
  if (
    question.length < 10
    || question.length > 1000
    || !["single", "split"].includes(locator.mode)
    || !validUnitIds
  ) {
    throw clientError({
      status: 0,
      code: "invalid_bill_reappropriation_locator",
      message: "Revise a descrição, o modo e as unidades do rateio.",
    });
  }
  return {
    billId: locator.billId,
    question,
    mode: locator.mode,
    buildingUnitIds: unitIds,
  };
};

export const fetchBillReappropriationHistory = async (workId, locator, options = {}) => {
  const { billId } = billReappropriationLocator(locator);
  const query = new URLSearchParams({
    context_type: "bill_reappropriation",
    context_id: String(billId),
  });
  return aiFetch(`${workPath(workId)}/analyses?${query}`, options);
};

export const createBillReappropriationAnalysis = async (
  workId,
  locator,
  { idempotencyKey, signal } = {},
) => {
  const { billId, question, mode, buildingUnitIds } = billReappropriationLocator(locator, {
    requireQuestion: true,
  });
  return aiFetch(`${workPath(workId)}/analyses`, {
    method: "POST",
    idempotencyKey,
    signal,
    body: {
      contextType: "bill_reappropriation",
      contextId: String(billId),
      questionKey: "suggest_bill_appropriation",
      question,
      constraints: { mode, buildingUnitIds },
    },
  });
};

export const fetchAiAnalysis = (workId, analysisId, options = {}) => (
  aiFetch(`${workPath(workId)}/analyses/${encodeURIComponent(String(analysisId))}`, options)
);

export const submitAiFeedback = (workId, analysisId, feedback, options = {}) => (
  aiFetch(`${workPath(workId)}/analyses/${encodeURIComponent(String(analysisId))}/feedback`, {
    ...options,
    method: "POST",
    body: feedback,
  })
);

export default {
  fetchAiAccess,
  fetchAiHistory,
  createAiAnalysis,
  fetchFinancialMonthAiHistory,
  createFinancialMonthAiAnalysis,
  fetchResourceGroupAiHistory,
  createResourceGroupAiAnalysis,
  fetchBillReappropriationHistory,
  createBillReappropriationAnalysis,
  fetchAiAnalysis,
  submitAiFeedback,
};
